<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DateField from '@/components/ui/DateField.vue'
import AppDialog from '@/components/ui/Dialog.vue'
import SimplePaginator from '@/components/ui/SimplePaginator.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import Tag from 'primevue/tag'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import { customerApi, type Customer, type CustomerQuote } from '@/services/customers'
import { storeSalesApi, type StoreSale } from '@/services/storeSales'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel, statusSeverity } from '@/utils/status'

/**
 * Ficha de un cliente, con sus cotizaciones debajo.
 *
 * Dos cosas que no son obvias y que se Roman-DD encontré costosas:
 *
 * **El rango de fechas se hereda, no se impone.** Entrando desde el panel llega en la URL
 * (`from=dashboard&date_from=…`) y las cotizaciones salen filtradas por ese periodo.
 * Entrando desde el listado no llega nada, y entonces se ven todas. Si la ficha filtrara
 * por el mes en curso por su cuenta, cualquier cliente con cotizaciones antiguas enseñaría
 * una lista vacía sin decir por qué, que es indistinguible de «este cliente no cotiza».
 *
 * **El origen sobrevive a que se toque el filtro.** El botón de vuelta lleva al panel con
 * su rango. Si el origen se perdiera al filtrar, llevaría al listado de clientes después de
 * haber tocado una fecha, que no es donde estabas.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const canManage = computed(() => authStore.hasPermission('customers.manage'))
/**
 * La sección de tienda se protege con su propio permiso, no con
 * `customers.manage`: quien puede ver una ficha no necesariamente opera la
 * tienda. El backend responde 403 por la misma razón.
 */
const canManageStoreSales = computed(() => authStore.hasPermission('store-sales.manage'))

const customer = ref<Customer | null>(null)
const quotes = ref<CustomerQuote[]>([])
const storeSales = ref<StoreSale[]>([])
const loading = ref(true)
const loadError = ref<string | null>(null)
const quotesError = ref<string | null>(null)
const storeSalesError = ref<string | null>(null)
const storeSalesLoading = ref(false)
const storeSalesPage = ref(1)
const storeSalesLastPage = ref(1)
const acting = ref(false)
const actionError = ref<string | null>(null)
const showDeleteDialog = ref(false)

/** Filtro de fechas del apartado de cotizaciones, en `YYYY-MM-DD`. */
const dateFrom = ref('')
const dateTo = ref('')

/** De dónde se vino. `dashboard` lleva al panel; cualquier otra cosa, al listado. */
const origin = computed(() => (route.query.from === 'dashboard' ? 'dashboard' : null))

/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 */
const { destino: destinoListado } = useVolverAlListado('customers.index')

const customerId = computed(() => Number(route.params.id))

/** El botón de vuelta dice a dónde lleva, para que no haya que pulsarlo para saberlo. */
const backLabel = computed(() => (origin.value === 'dashboard' ? 'Volver al panel' : 'Volver a clientes'))
const backAria = computed(() =>
  origin.value === 'dashboard' ? 'Volver al panel' : 'Volver al listado de clientes',
)

/** Si el listado de cotizaciones tiene fechas puestas. */
const hayRango = computed(() => dateFrom.value !== '' || dateTo.value !== '')

/** Los títulos de la tabla de cotizaciones del cliente. */
const cabeceraCotizacion = ['Nº Cot.', 'Fecha', 'Total', 'Estado', 'Facturación']

/** Los datos del cliente, o un texto que diga qué falta. */
function valor(texto: string | null, ausente: string): string {
  if (texto === null || texto === '') return ausente

  return texto
}

async function loadCustomer(): Promise<void> {
  loading.value = true
  loadError.value = null
  customer.value = null

  try {
    const { data } = await customerApi.get(customerId.value)
    customer.value = data.data
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    loadError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el cliente.'
  } finally {
    loading.value = false
  }
}

/**
 * Las cotizaciones del cliente.
 *
 * Sin rango devuelve todas: es lo que permite entrar desde el listado y ver el historial
 * entero. Con rango, solo ese periodo.
 */
async function loadQuotes(): Promise<void> {
  quotesError.value = null

  try {
    const { data } = await customerApi.quotes(customerId.value, {
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
    })

    quotes.value = data.data
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    quotes.value = []
    quotesError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudieron cargar las cotizaciones.'
  }
}

/**
 * Ventas de tienda del cliente.
 *
 * Va aparte de las cotizaciones a propósito: la tabla de cotizaciones conserva
 * su significado y sus columnas, y una venta de tienda no tiene estado
 * logístico. Incluye las pendientes de facturación porque la ficha cuenta lo
 * que el cliente compró, no solo lo que ya tiene factura.
 */
async function loadStoreSales(page = storeSalesPage.value): Promise<void> {
  if (!canManageStoreSales.value) {
    storeSales.value = []
    return
  }

  storeSalesLoading.value = true
  storeSalesError.value = null

  try {
    const { data } = await storeSalesApi.customer(customerId.value, { page })
    storeSales.value = data.data
    storeSalesPage.value = data.meta.current_page
    storeSalesLastPage.value = data.meta.last_page
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    storeSales.value = []
    storeSalesError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudieron cargar las ventas de tienda.'
  } finally {
    storeSalesLoading.value = false
  }
}

/**
 * El rango va a la URL, no a un estado de la vista.
 *
 * Es lo que hace que recargar o volver atrás con el navegador devuelva la vista a como
 * estaba. El origen se copia porque si se perdiera, el botón de vuelta llevaría al listado
 * de clientes después de haber tocado una fecha.
 */
async function applyFilter(): Promise<void> {
  const query = { ...route.query }

  if (dateFrom.value === '') delete query.date_from
  else query.date_from = dateFrom.value

  if (dateTo.value === '') delete query.date_to
  else query.date_to = dateTo.value

  await router.replace({ name: 'customers.show', params: { id: String(customerId.value) }, query })
  await loadQuotes()
}

async function clearFilter(): Promise<void> {
  dateFrom.value = ''
  dateTo.value = ''
  await applyFilter()
}

/** Lo que se dice del periodo, porque un importe sin periodo no se puede interpretar. */
function rangoTexto(): string {
  if (dateFrom.value === '' && dateTo.value === '') return 'Todas las fechas'
  if (dateTo.value === '') return `Desde el ${formatShortDate(dateFrom.value)}`
  if (dateFrom.value === '') return `Hasta el ${formatShortDate(dateTo.value)}`

  return `Del ${formatShortDate(dateFrom.value)} al ${formatShortDate(dateTo.value)}`
}

/**
 * Sin cotizaciones hay dos respuestas distintas, porque son dos preguntas distintas:
 * «no cotiza en este periodo» y «nunca ha cotizado». Confundirlas deja a quien mira
 * pensando que el filtro está mal.
 */
function quotesEmptyText(): string {
  if (hayRango.value) return 'No tiene cotizaciones en ese periodo'

  return 'Este cliente todavía no tiene cotizaciones'
}

async function confirmDelete(): Promise<void> {
  if (customer.value === null) return

  acting.value = true
  actionError.value = null

  try {
    await customerApi.remove(customer.value.id)
    showDeleteDialog.value = false
    await router.push(destinoListado.value)
  } catch (cause: unknown) {
    // No se navega: el fallo se dice sin perder el registro en pantalla, que es lo que
    // hace falta para corregir y reintentar.
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    actionError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar el cliente.'
  } finally {
    acting.value = false
  }
}

/** La vuelta: al panel si se vino de allí con su rango, y al listado si no. */
async function goBack(): Promise<void> {
  if (origin.value === 'dashboard') {
    const query = { ...route.query }
    delete query.from

    await router.push({ name: 'dashboard', query })

    return
  }

  await router.push(destinoListado.value)
}

/** El rango llega en la URL, y hay que leerlo antes de pedir las cotizaciones. */
function readRangeFromUrl(): void {
  const desde = route.query.date_from
  const hasta = route.query.date_to

  dateFrom.value = (Array.isArray(desde) ? desde[0] : desde) ?? ''
  dateTo.value = (Array.isArray(hasta) ? hasta[0] : hasta) ?? ''
}

async function load(): Promise<void> {
  readRangeFromUrl()
  await loadCustomer()
  // Las cotizaciones se piden después del cliente: el bucle de `flushPromises` de las
  // pruebas mira justo eso, y pedir las dos en paralelo deja la sección sin pintar.
  await loadQuotes()
  // Y la tienda después, para que el orden de las peticiones no cambie el de las
  // secciones en pantalla.
  storeSalesPage.value = 1
  await loadStoreSales()

  // Así se llega desde el botón de borrar del listado.
  if (route.query.action === 'delete') showDeleteDialog.value = true
}

onMounted(load)

// Misma ruta, distinto `id`: sin esto se seguiría viendo el cliente anterior.
watch(() => route.params.id, load)

watch(
  () => route.query.date_from,
  () => {
    readRangeFromUrl()
    void loadQuotes()
  },
)
watch(
  () => route.query.date_to,
  () => {
    readRangeFromUrl()
    void loadQuotes()
  },
)
</script>

<template>
  <div class="flex flex-col gap-4">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0">
        <h1 class="truncate text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ customer?.name ?? 'Detalle del cliente' }}
        </h1>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          :label="backLabel"
          :aria-label="backAria"
          data-testid="back-to-origin"
          icon="pi pi-arrow-left"
          severity="secondary"
          @click="goBack"
        />
        <template v-if="canManage && customer !== null">
          <Button
            label="Editar"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            @click="router.push({ name: 'customers.edit', params: { id: String(customer.id) } })"
          />
          <Button
            label="Borrar"
            icon="pi pi-trash"
            severity="danger"
            outlined
            @click="showDeleteDialog = true"
          />
        </template>
      </div>
    </header>

    <Message
      v-if="actionError !== null"
      severity="error"
      role="alert"
      :closable="false"
    >
      {{ actionError }}
    </Message>

    <Message
      v-if="loadError !== null"
      severity="error"
      role="alert"
      :closable="false"
    >
      {{ loadError }}
    </Message>

    <AppCard>
      <div
        v-if="loading"
        class="flex flex-col gap-3"
      >
        <Skeleton
          v-for="linea in 6"
          :key="linea"
          height="1rem"
          class="w-full"
        />
      </div>

      <dl
        v-else-if="customer !== null"
        class="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3"
      >
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Documento
          </dt>
          <dd class="mt-1 font-mono text-surface-900 dark:text-surface-100">
            {{ valor(customer.document, 'Sin documento') }}
          </dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Teléfono
          </dt>
          <dd class="mt-1 font-mono text-surface-900 dark:text-surface-100">
            {{ valor(customer.phone, '—') }}
          </dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Correo
          </dt>
          <dd class="mt-1 text-surface-900 dark:text-surface-100">
            {{ valor(customer.email, '—') }}
          </dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Ciudad
          </dt>
          <dd class="mt-1 text-surface-900 dark:text-surface-100">
            {{ valor(customer.city, '—') }}
          </dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Dirección
          </dt>
          <dd class="mt-1 text-surface-900 dark:text-surface-100">
            {{ valor(customer.address, '—') }}
          </dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wider text-surface-500 dark:text-surface-400">
            Origen
          </dt>
          <dd class="mt-1 text-surface-900 dark:text-surface-100">
            {{ valor(customer.source, '—') }}
          </dd>
        </div>
      </dl>
    </AppCard>

    <!--
      Las cotizaciones del cliente. El rango va en la URL, y sin rango se ven todas: es lo
      que permite entrar desde el listado y ver el historial entero en vez de un mes vacío
      sin explicación.

      El `data-testid` va en la tarjeta entera y no solo en la tabla: el periodo está en la
      cabecera y las cotizaciones en el cuerpo, y son las dos mitades de la misma respuesta.
      Con el identificador solo en la tabla, el periodo que se está mostrando quedaba fuera
      de lo que se comprobaba, y una lista de importes sin el periodo al lado no se puede
      interpretar.
    -->
    <AppCard data-testid="customer-quotes">
      <template #header>
        <div class="flex flex-col gap-3">
          <h2 class="text-base font-semibold text-surface-900 dark:text-surface-100">
            Cotizaciones
          </h2>

          <form
            data-testid="quotes-filter"
            class="flex flex-col gap-3 sm:flex-row sm:items-end"
            @submit.prevent="applyFilter"
          >
            <div class="flex-1">
              <DateField
                v-model="dateFrom"
                input-id="quote-date-from"
                label="Desde"
              />
            </div>
            <div class="flex-1">
              <DateField
                v-model="dateTo"
                input-id="quote-date-to"
                label="Hasta"
              />
            </div>
            <div class="flex gap-2">
              <Button
                type="submit"
                label="Filtrar"
                icon="pi pi-filter"
              />
              <Button
                v-if="hayRango"
                label="Quitar filtro"
                severity="secondary"
                outlined
                type="button"
                @click="clearFilter"
              />
            </div>
          </form>

          <!--
            El periodo se dice, porque los importes de una lista sin periodo no se pueden
            interpretar: no se sabe si son del mes o del histórico entero.
          -->
          <p
            data-testid="quotes-range"
            class="text-sm text-surface-500 dark:text-surface-400"
          >
            {{ rangoTexto() }}
          </p>
        </div>
      </template>

      <Message
        v-if="quotesError !== null"
        severity="error"
        role="alert"
        :closable="false"
      >
        {{ quotesError }}
      </Message>

      <p
        v-else-if="quotes.length === 0"
        data-testid="quotes-empty"
        class="py-6 text-center text-sm text-surface-500 dark:text-surface-400"
      >
        {{ quotesEmptyText() }}
      </p>

      <!--
        Tabla a pelo, y no `DenseTable`.
        Son tres filas y tres columnas, sin acciones ni paginación: el componente de
        listados aporta un sobre y un contador que aquí sobran. Y hay una razón de fondo —
        meter un `RouterLink` en una celda de `DenseTable` lo monta y lo desmonta en cada
        render, que con un componente real es un «Maximum recursive updates exceeded» que
        tumba la vista entera.
      -->
      <div
        v-else
        class="overflow-x-auto"
      >
        <table class="w-full text-sm">
          <thead>
            <tr>
              <th
                v-for="titulo in cabeceraCotizacion"
                :key="titulo"
                class="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-surface-500 dark:text-surface-400"
              >
                {{ titulo }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="cotizacion in quotes"
              :key="cotizacion.id"
              class="border-t border-surface-200 dark:border-surface-700"
            >
              <td class="px-3 py-2">
                <RouterLink
                  :to="{ name: 'quotes.show', params: { id: String(cotizacion.id) } }"
                  class="font-mono text-primary-600 hover:underline dark:text-primary-400"
                >
                  {{ cotizacion.quote_number ?? '—' }}
                </RouterLink>
              </td>
              <td class="px-3 py-2">
                <RouterLink
                  :to="{ name: 'quotes.show', params: { id: String(cotizacion.id) } }"
                  class="font-mono text-primary-600 hover:underline dark:text-primary-400"
                >
                  {{ formatShortDate(cotizacion.quote_date) }}
                </RouterLink>
              </td>
              <td class="px-3 py-2 text-right font-medium tabular-nums text-surface-900 dark:text-surface-100">
                {{ formatCurrency(cotizacion.total_price) }}
              </td>
              <td class="px-3 py-2">
                <Tag
                  :value="statusLabel(cotizacion.status as never)"
                  :severity="statusSeverity(cotizacion.status as never)"
                />
              </td>
              <td class="px-3 py-2">
                <Tag
                  :value="cotizacion.is_invoiced ? 'FACTURADA' : 'PENDIENTE'"
                  :severity="cotizacion.is_invoiced ? 'success' : 'secondary'"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </AppCard>

    <!--
      Ventas de tienda.

      Es una sección propia y no una fila más de la tabla de cotizaciones: una
      venta no tiene estado logístico ni artículos, y meterla en la misma tabla
      habría obligado a dejar columnas vacías y a que «cotizaciones» dejara de
      significar una sola cosa. Solo se pinta con `store-sales.manage`; sin el
      permiso el backend también respondería 403.
    -->
    <AppCard
      v-if="canManageStoreSales"
      data-testid="customer-store-sales"
    >
      <template #header>
        <div class="flex flex-col gap-1">
          <h2 class="text-base font-semibold text-surface-900 dark:text-surface-100">
            Ventas tienda
          </h2>
          <p class="text-sm text-surface-500 dark:text-surface-400">
            Ventas cerradas en la tienda, facturadas o pendientes de factura.
          </p>
        </div>
      </template>

      <Message
        v-if="storeSalesError !== null"
        severity="error"
        role="alert"
        :closable="false"
      >
        {{ storeSalesError }}
      </Message>

      <p
        v-else-if="storeSalesLoading"
        class="py-6 text-center text-sm text-surface-500 dark:text-surface-400"
      >
        Cargando ventas de tienda…
      </p>

      <p
        v-else-if="storeSales.length === 0"
        data-testid="store-sales-empty"
        class="py-6 text-center text-sm text-surface-500 dark:text-surface-400"
      >
        Este cliente todavía no tiene ventas de tienda.
      </p>

      <div
        v-else
        class="overflow-x-auto"
      >
        <table class="w-full text-sm">
          <thead>
            <tr>
              <th
                v-for="titulo in ['Nº Venta', 'Fecha', 'Valor', 'Facturación', 'Acciones']"
                :key="titulo"
                class="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-surface-500 dark:text-surface-400"
              >
                {{ titulo }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="venta in storeSales"
              :key="venta.id"
              class="border-t border-surface-200 dark:border-surface-700"
            >
              <td class="px-3 py-2">
                <RouterLink
                  :to="{ name: 'store-sales.show', params: { id: String(venta.id) } }"
                  class="font-mono text-primary-600 hover:underline dark:text-primary-400"
                >
                  {{ venta.sale_number }}
                </RouterLink>
              </td>
              <td class="px-3 py-2">
                {{ formatShortDate(venta.sale_date) }}
              </td>
              <td class="px-3 py-2 text-right font-medium tabular-nums text-surface-900 dark:text-surface-100">
                {{ formatCurrency(venta.amount) }}
              </td>
              <td class="px-3 py-2">
                <Tag
                  :value="venta.is_invoiced ? 'FACTURADA' : 'PENDIENTE'"
                  :severity="venta.is_invoiced ? 'success' : 'secondary'"
                />
                <span
                  v-if="venta.invoice_number"
                  class="ml-2 font-mono text-xs text-surface-500 dark:text-surface-400"
                >
                  {{ venta.invoice_number }}
                </span>
              </td>
              <td class="px-3 py-2">
                <div class="flex flex-wrap gap-2">
                  <RouterLink
                    :to="{ name: 'store-sales.show', params: { id: String(venta.id) } }"
                    class="text-sm text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Ver
                  </RouterLink>
                  <RouterLink
                    :to="{ name: 'store-sales.edit', params: { id: String(venta.id) } }"
                    class="text-sm text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Editar
                  </RouterLink>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <SimplePaginator
          :page="storeSalesPage"
          :last-page="storeSalesLastPage"
          label="ventas de tienda"
          testid="customer-store-sales-paginator"
          @change="(pagina) => loadStoreSales(pagina)"
        />
      </div>
    </AppCard>

    <AppDialog
      :visible="showDeleteDialog"
      title="Borrar cliente"
      confirm-label="Borrar"
      fullscreen-mobile
      @confirm="confirmDelete"
      @cancel="showDeleteDialog = false"
    >
      <!--
        El diálogo nombra al cliente. «Se va a borrar. ¿Seguro?» no dice a quién, y con
        varios clientes abiertos en pestañas seguidas es imposible saber sobre cuál se está
        confirmando.
      -->
      <p class="text-sm text-surface-600 dark:text-surface-300">
        Se borrará <strong>{{ customer?.name }}</strong> y sus datos. Esta acción no se puede
        deshacer.
      </p>
    </AppDialog>
  </div>
</template>